"use client";

import { useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { CreditCard } from "lucide-react";
import {PublicOfferSummary} from "./PublicOfferSummary";
import {PublicOfferDetailsTrigger} from "./PublicOfferDetailsTrigger";
import { publicPricingStyles } from "./publicEntryStyles";
import { PricingConditions } from "./PublicPricingTable";
import { useTranslations } from "next-intl";
import { useTranslation } from "../../../hooks/useTranslation";
import {
  CLASS_COLOR_NEUTRAL,
  getClassColorAccentToken,
  normalizeClassColorKey,
} from "../../../lib/classColors";
import { formatPriceMinor } from "../../../lib/formatPriceMinor";
import { cn } from "../../../lib/utils";
import type {
  PublicAccessPass,
  PublicPricingClass,
} from "../../../services/public-passes.service";
import { LowestPrice30dNote } from "../promotions/PriceNotes";
import { CardPriceLine } from "./PublicCardPriceLine";
import {
  uniquePricingClasses,
  classEntryPrice,
  classPasses,
} from "./publicPricingModel";
import type { ClassEntryPrice } from "./publicPricingModel";

import {SectionAccordion} from "@fitro/customer-ui/SectionAccordion";
import {AcceptedPassList} from "../shared/AcceptedPassList";
import {BookingRulesSection} from "../shared/BookingRulesSection";

const NOTE_CLASS = "text-[11px] leading-4 text-[var(--landing-neutral-500)]";
const FACT_NOTE_CLASS = "mt-0.5 text-xs font-normal leading-4 text-[var(--landing-neutral-500)]";

/** Prices are shared only when every displayed entry condition is equivalent. */
export function PublicClassPricing({
  classes,
  passes,
}: {
  classes: PublicPricingClass[];
  passes: PublicAccessPass[];
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const { locale } = useTranslation("landing");
  const compact = useTranslations("landing.publicEntry.pricing.compact");
  const passesById = useMemo(
    () => new Map(passes.map((pass) => [pass.id, pass])),
    [passes],
  );
  const unique = uniquePricingClasses(classes);
  const groups = new Map<
    string,
    {
      key: string;
      entry: ClassEntryPrice;
      items: PublicPricingClass[];
      currency: string;
    }
  >();
  for (const item of unique) {
    const entry = classEntryPrice(item);
    const currency = item.currency ?? "PLN";
    const benefits = item.benefitCardPrices
      .map((line) => ({
        cardName: line.cardName,
        priceMinor: line.priceMinor,
        cards: line.cards
          .map((card) => ({ provider: card.provider, variant: card.variant }))
          .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
      }))
      .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    const key = JSON.stringify({
      entry,
      currency,
      benefits,
      newCustomer: item.newCustomerPromotion,
    });
    const existing = groups.get(key);
    if (existing) existing.items.push(item);
    else groups.set(key, { key, entry, currency, items: [item] });
  }
  const rank = (entry: ClassEntryPrice) =>
    entry.kind === "paid" ? 0 : entry.kind === "passes" ? 1 : 2;
  const sorted = [...groups.values()].sort(
    (a, b) =>
      rank(a.entry) - rank(b.entry) ||
      a.currency.localeCompare(b.currency) ||
      (a.entry.kind === "paid" && b.entry.kind === "paid"
        ? a.entry.priceMinor - b.entry.priceMinor
        : 0) ||
      a.key.localeCompare(b.key),
  );
  return (
    <div className="xl:overflow-hidden xl:rounded-xl xl:border xl:border-slate-200/80">
      <table
        aria-label={compact("entriesHeading")}
        className="block w-full table-fixed text-left text-sm xl:table"
      >
        <colgroup className="hidden xl:table-column-group">
          <col className="w-[20%]" />
          <col />
        </colgroup>
        <thead
          className={cn(
            publicPricingStyles.tableHeader,
            "hidden xl:table-header-group",
          )}
        >
          <tr>
            <th scope="col" className="px-6 py-3 text-sm font-medium">
              {compact("singleEntryColumn")}
            </th>
            <th scope="col" className="px-6 py-3 text-sm font-medium">
              {compact("classColumn")}
            </th>
          </tr>
        </thead>
        {sorted.map((group) => (
          <tbody
            key={group.key}
            className="mb-4 block overflow-hidden rounded-xl border border-slate-200/80 bg-white last:mb-0 xl:mb-0 xl:table-row-group xl:rounded-none xl:border-0 xl:border-b xl:border-slate-100 xl:even:bg-slate-50 xl:last:border-b-0"
          >
            <tr className="block xl:table-row">
              <th
                scope="row"
                className="mx-3 block border-b border-slate-200/70 py-4 text-center align-top font-normal sm:mx-5 xl:table-cell xl:border-0 xl:px-6 xl:text-left"
              >
                <ClassEntryPanel
                  pricingClass={group.items[0]}
                  entry={group.entry}
                />
              </th>
              <td className="block px-3 align-top sm:px-5 xl:table-cell xl:px-6">
                <ul className="divide-y divide-slate-200/70">
                  {[...group.items]
                    .sort((a, b) => a.name.localeCompare(b.name, locale))
                    .map((item) => {
                      const accepted = classPasses(item, passesById);
                      const preview = (<PublicOfferDetailsTrigger triggerLabel={item.name} name={item.name} description={item.description} fullDescriptionHtml={item.fullDescriptionHtml}
                                className="min-h-16 py-4 text-left text-lg font-semibold leading-7 underline-offset-4 hover:underline sm:text-xl"
                                summary={<PublicOfferSummary instructors={item.instructors} locations={item.locations} price={<ClassEntryFactValue pricingClass={item} entry={classEntryPrice(item)}/>}/>}>
                                <BookingRulesSection rules={item.bookingRules} inline/>
                              </PublicOfferDetailsTrigger>);
                      return (
                        <li
                          key={item.id}
                          className="relative min-w-0 pl-5 [overflow-wrap:anywhere]"
                          style={accentStyle(item.colorKey)}
                        >
                          <span
                            aria-hidden="true"
                            className="absolute left-0 top-4 h-7 w-1 rounded"
                            style={{
                              backgroundColor: "var(--public-pricing-accent)",
                            }}
                          />
                          {accepted.length ? <SectionAccordion variant="pricing" title={item.name} isOpen={expanded.has(item.id)} onToggle={() => setExpanded(previous => {
                            const next = new Set(previous);
                            if (next.has(item.id)) next.delete(item.id); else next.add(item.id);
                            return next;
                          })}>
                            {expanded.has(item.id) ? <div className="space-y-4">
                              <p className="text-sm leading-5 text-[var(--landing-neutral-500)]">
                                {compact(classEntryPrice(item).kind === "passes" ? "acceptedPassesOnlyIntro" : "acceptedPassesIntro")}
                              </p>
                              <AcceptedPassList passes={accepted}/>
                            </div> : null}
                          </SectionAccordion> : preview}
                        </li>
                      );
                    })}
                </ul>
              </td>
            </tr>
          </tbody>
        ))}
      </table>
    </div>
  );
}

function ClassEntryPanel({
  pricingClass,
  entry,
}: {
  pricingClass: PublicPricingClass;
  entry: ClassEntryPrice;
}) {
  const { locale, t } = useTranslation("landing");
  const money = (amount: number) =>
    formatPriceMinor(amount, pricingClass.currency ?? "PLN", locale);
  const panel = "flex min-w-0 flex-col items-center gap-2 xl:items-start";

  if (entry.kind === "free") {
    return (
      <div className={panel}>
        <p className={cn("font-semibold text-emerald-700", "text-xl sm:text-2xl leading-7")}>
          {t("publicEntry.pricing.classes.free")}
        </p>
      </div>
    );
  }
  if (entry.kind === "passes") {
    return (
      <div className="flex min-w-0 items-center justify-center gap-2 text-[var(--landing-neutral-700)] xl:justify-start">
        <CreditCard
          className="h-5 w-5 shrink-0 text-[var(--landing-primary-500)]"
          aria-hidden="true"
        />
        <p className="font-semibold text-xl sm:text-2xl leading-7">
          {t("publicEntry.pricing.classes.passesOnly")}
        </p>
      </div>
    );
  }

  const { promotion } = entry;
  const newCustomer = pricingClass.newCustomerPromotion;
  return (
    <div className={panel}>
      <p className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 xl:justify-start">
        {promotion ? (
          <s className="self-end text-[14px] tabular-nums text-[var(--landing-neutral-400)]">
            {money(promotion.lowestPrice30dMinor)}
          </s>
        ) : null}
        <b
          className={cn(
            "shrink-0 whitespace-nowrap font-semibold leading-7 tabular-nums text-[26px] sm:text-[28px]",
            promotion ? "text-indigo-700" : "text-[var(--landing-neutral-900)]",
          )}
        >
          {money(entry.priceMinor)}
        </b>
      </p>
      {newCustomer ? (
        <p className="text-[13px] font-normal leading-4 text-emerald-700">
          {newCustomerLine(t, newCustomer, money)}
        </p>
      ) : null}
      {/* A card price is its own price, never a reduction (ADR 0015). */}
      {pricingClass.benefitCardPrices.length > 0 ? (
        <div className="mt-1 space-y-2">
          <div
            aria-hidden="true"
            className="mx-auto mb-3 h-px w-10 bg-slate-200/70"
          />
          {pricingClass.benefitCardPrices.map((line) => (
            <CardPriceLine
              key={line.cardName}
              line={line}
              price={money(line.priceMinor)}
            />
          ))}
        </div>
      ) : null}
      {promotion || typeof newCustomer?.lowestPrice30dMinor === "number" ? (
        <PricingConditions>
          {promotion ? (
            <LowestPrice30dNote
              price={money(promotion.lowestPrice30dMinor)}
              className={NOTE_CLASS}
            />
          ) : null}
          {typeof newCustomer?.lowestPrice30dMinor === "number" ? (
            <LowestPrice30dNote
              price={money(newCustomer.lowestPrice30dMinor)}
              className={NOTE_CLASS}
            />
          ) : null}
        </PricingConditions>
      ) : null}
    </div>
  );
}

/** The price fact of a class's details window: the same lines as a class page, quieter than the pricing table. */
function ClassEntryFactValue({
  pricingClass,
  entry,
}: {
  pricingClass: PublicPricingClass;
  entry: ClassEntryPrice;
}) {
  const { locale, t } = useTranslation("landing");
  const money = (amount: number) =>
    formatPriceMinor(amount, pricingClass.currency ?? "PLN", locale);
  if (entry.kind === "free") return <>{t("publicEntry.pricing.classes.free")}</>;
  if (entry.kind === "passes") return <>{t("publicEntry.events.passesOnly")}</>;
  const { promotion } = entry;
  const newCustomer = pricingClass.newCustomerPromotion;
  return (
    <>
      <span className="tabular-nums">
        {promotion ? <s className="mr-2 font-normal text-[var(--landing-neutral-500)]">{money(promotion.lowestPrice30dMinor)}</s> : null}
        {money(entry.priceMinor)}
      </span>
      {promotion ? <LowestPrice30dNote price={money(promotion.lowestPrice30dMinor)} className={FACT_NOTE_CLASS} /> : null}
      {newCustomer ? <p className="mt-0.5 text-xs font-normal leading-4 text-emerald-700">{newCustomerLine(t, newCustomer, money)}</p> : null}
      {typeof newCustomer?.lowestPrice30dMinor === "number" ? <LowestPrice30dNote price={money(newCustomer.lowestPrice30dMinor)} className={FACT_NOTE_CLASS} /> : null}
      {/* A card price is its own price, never a reduction (ADR 0015). */}
      {pricingClass.benefitCardPrices.map((line) => (
        <p key={line.cardName} className={FACT_NOTE_CLASS}>
          {line.priceMinor === 0
            ? t("publicEntry.events.benefitCardFree", { card: line.cardName })
            : t("publicEntry.events.benefitCard", { card: line.cardName, amount: money(line.priceMinor) })}
        </p>
      ))}
    </>
  );
}

function newCustomerLine(
  t: ReturnType<typeof useTranslation>["t"],
  newCustomer: NonNullable<PublicPricingClass["newCustomerPromotion"]>,
  money: (amount: number) => string,
) {
  return t(
    newCustomer.eligibility === "FIRST_CLASS_PURCHASE"
      ? "publicEntry.pricing.classes.firstClass"
      : "publicEntry.pricing.newCustomerPromotion",
    { price: money(newCustomer.finalAmountMinor) },
  );
}

function accentStyle(colorKey: string | null | undefined): CSSProperties {
  const key = normalizeClassColorKey(colorKey);
  return {
    "--public-pricing-accent":
      key && key !== CLASS_COLOR_NEUTRAL
        ? getClassColorAccentToken(key)
        : "var(--landing-primary-500)",
  } as CSSProperties;
}
